// Browser MediaRecorder API used to record audio for WoundScribe.

import { WOUNDSCRIBE_MIC_STATES } from 'woundscribe/core/constants';
import type { RecordingGeolocation } from 'woundscribe/types';

const fallbackAudioMimeType: string = 'audio/webm';
const fallbackAudioExtension: string = 'webm';
const audioChunkDuration: number = 250;
export const fallbackAudioMime: PreferredAudioMime = {
  mimeType: fallbackAudioMimeType,
  extension: fallbackAudioExtension,
};

export type PreferredAudioMime = {
  mimeType: string;
  extension: string;
};

export type RecordingSession = {
  stream: MediaStream;
  recorder: MediaRecorder;
  mimeType: string;
  extension: string;
  chunks: BlobPart[];
};

export function pickAudioMimeType(): PreferredAudioMime {
  const candidates: PreferredAudioMime[] = [
    { mimeType: 'audio/webm;codecs=opus', extension: 'webm' },
    { mimeType: 'audio/webm', extension: 'webm' },
    { mimeType: 'audio/mp4', extension: 'm4a' },
    { mimeType: 'audio/ogg;codecs=opus', extension: 'ogg' },
    { mimeType: 'audio/ogg', extension: 'ogg' },
  ];

  if (typeof MediaRecorder === 'undefined') {
    return fallbackAudioMime;
  }

  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate.mimeType)) {
      return candidate;
    }
  }

  return fallbackAudioMime;
}

/**
 * Asks the browser for the current position. When the site has no location decision yet,
 * the browser shows its own permission prompt, and that prompt waits for the clinician.
 * No timeout is set on purpose: the clinician decides how long to take. The promise
 * resolves to null when the caller aborts (for example, Cancel), and a browser answer
 * that arrives later is ignored. It never rejects, so location cannot block the recording.
 */
export function requestRecordingGeolocation(
  signal?: AbortSignal
): Promise<RecordingGeolocation | null> {
  const unavailable = (): RecordingGeolocation => ({
    status: 'UNAVAILABLE',
    latitude: null,
    longitude: null,
  });
  if (!globalThis.navigator?.geolocation) {
    return Promise.resolve(unavailable());
  }
  if (signal?.aborted) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: RecordingGeolocation | null) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(value);
    };
    signal?.addEventListener('abort', () => finish(null), { once: true });

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const { latitude, longitude } = coords;
        finish(
          Number.isFinite(latitude) && Number.isFinite(longitude)
            ? { status: 'CAPTURED', latitude, longitude }
            : unavailable()
        );
      },
      (error) => {
        finish({
          status:
            error.code === error.PERMISSION_DENIED ? 'DENIED' : 'UNAVAILABLE',
          latitude: null,
          longitude: null,
        });
      }
    );
  });
}

export async function createRecordingSession(opts: {
  onStreamEnded?: () => void;
  audioBitsPerSecond?: number;
  onChunk?: (chunk: Blob) => void;
}): Promise<RecordingSession> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Microphone capture is not supported in this browser.');
  }
  if (typeof MediaRecorder === 'undefined') {
    throw new TypeError('Audio recording is not supported in this browser.');
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });

  const { mimeType, extension } = pickAudioMimeType();
  const audioBitsPerSecond = opts.audioBitsPerSecond;
  const recorderOptions: MediaRecorderOptions = {};
  if (mimeType) {
    recorderOptions.mimeType = mimeType;
  }
  if (audioBitsPerSecond && audioBitsPerSecond > 0) {
    recorderOptions.audioBitsPerSecond = audioBitsPerSecond;
  }
  const recorder = new MediaRecorder(stream, recorderOptions);

  const session: RecordingSession = {
    stream,
    recorder,
    mimeType: recorder.mimeType || mimeType,
    extension,
    chunks: [],
  };

  recorder.ondataavailable = (event: BlobEvent) => {
    if (event.data && event.data.size > 0) {
      session.chunks.push(event.data);
      opts.onChunk?.(event.data);
    }
  };

  recorder.onerror = () => {
    opts.onStreamEnded?.();
  };

  session.stream.getAudioTracks().forEach((track) => {
    track.addEventListener('ended', () => {
      opts.onStreamEnded?.();
    });
  });

  // audioChunkDuration timeslice keeps chunks flowing for reliable stop on Safari and
  // matches the granularity needed for future streaming to the backend.
  recorder.start(audioChunkDuration);
  return session;
}

export function pauseRecordingSession(session: RecordingSession) {
  if (session.recorder.state === WOUNDSCRIBE_MIC_STATES.RECORDING) {
    session.recorder.pause();
  }
}

export function resumeRecordingSession(session: RecordingSession) {
  if (session.recorder.state === WOUNDSCRIBE_MIC_STATES.PAUSED) {
    session.recorder.resume();
  }
}

export function stopRecordingSession(session: RecordingSession): Promise<{
  blob: Blob;
  mimeType: string;
  extension: string;
}> {
  return new Promise((resolve, reject) => {
    const finish = () => {
      stopMediaTracks(session.stream);
      const mimeType = session.mimeType || fallbackAudioMimeType;
      const blob = new Blob(session.chunks, { type: mimeType });
      resolve({
        blob,
        mimeType,
        extension: session.extension || fallbackAudioExtension,
      });
    };

    if (session.recorder.state === WOUNDSCRIBE_MIC_STATES.INACTIVE) {
      finish();
      return;
    }

    session.recorder.onerror = () => {
      stopMediaTracks(session.stream);
      reject(new Error('MediaRecorder failed while stopping.'));
    };

    session.recorder.onstop = () => {
      finish();
    };

    try {
      // Flush any remaining data before stop
      if (
        session.recorder.state === WOUNDSCRIBE_MIC_STATES.RECORDING ||
        session.recorder.state === WOUNDSCRIBE_MIC_STATES.PAUSED
      ) {
        try {
          session.recorder.requestData();
        } catch {
          // Safari may not support requestData
        }
        session.recorder.stop();
      } else {
        finish();
      }
    } catch (err) {
      stopMediaTracks(session.stream);
      reject(err);
    }
  });
}

export function abortRecordingSession(session: RecordingSession | null) {
  if (!session) {
    return;
  }

  try {
    if (session.recorder.state !== WOUNDSCRIBE_MIC_STATES.INACTIVE) {
      session.recorder.stop();
    }
  } catch {
    // biome-ignore lint/suspicious/noConsole: reason here
    console.warn('Unable to abort the recording session');
  }
  stopMediaTracks(session.stream);
  session.chunks = [];
}

function stopMediaTracks(stream: MediaStream) {
  stream.getTracks().forEach((track) => {
    try {
      track.stop();
    } catch {
      // biome-ignore lint/suspicious/noConsole: reason here
      console.warn('Unable to stop media tracks');
    }
  });
}
